import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import CommonService from "../../services/common.service";
import { API_URLS, API_BASE_URL } from "../../constants/api-urls";
import "./WinningBidReview.scss";
import LinkedLogisticsPanel from "../../components/moduleLogistics/LinkedLogisticsPanel";

const API_BASE_URL_MEDIA = API_BASE_URL.replace("/api/v1", "");

export default function WinningBidReview() {
  const { auctionId } = useParams();
  const navigate = useNavigate();
  const [auction, setAuction] = useState(null);
  const [bids, setBids] = useState([]);
  const [winningBid, setWinningBid] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");

  useEffect(() => {
    fetchAuctionAndBids();
  }, [auctionId]);

  useEffect(() => {
    if (
      !auction ||
      (auction.winner_approval_status !== "pending" &&
        auction.status !== "pending_winner_approval" &&
        !(auction.winner_approval_status === "approved" && auction.status !== "ended"))
    ) {
      return undefined;
    }
    const interval = setInterval(() => {
      fetchAuctionAndBids();
    }, 15000);
    return () => clearInterval(interval);
  }, [auction?.winner_approval_status, auction?.status, auction?.winning_bid_id, auctionId]);

  useEffect(() => {
    if (auction && auction.seller_decision_deadline) {
      const interval = setInterval(() => {
        calculateTimeRemaining();
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [auction]);

  const fetchAuctionAndBids = async () => {
    try {
      setLoading(true);
      
      // Fetch auction details
      const auctionResponse = await CommonService.get(`${API_URLS.AUCTION.BASE}/${auctionId}`);
      if (auctionResponse && auctionResponse.success) {
        setAuction(auctionResponse.data);
        calculateTimeRemaining(auctionResponse.data);
      }

      // Fetch bids for this auction
      const bidsResponse = await CommonService.get(API_URLS.AUCTION.BIDS(auctionId));
      if (bidsResponse && bidsResponse.success && bidsResponse.data) {
        const allBids = Array.isArray(bidsResponse.data) ? bidsResponse.data : [];
        setBids(allBids);
        
        // Find winning bid (highest bid or bid marked as winning)
        const highestBid = allBids.find(b => b.is_highest_bid === 'Y' || b.is_highest_bid === 'y') || 
                          (allBids.length > 0 ? allBids.sort((a, b) => 
                            parseFloat(b.bid_amount || b.amount || 0) - parseFloat(a.bid_amount || a.amount || 0)
                          )[0] : null);
        setWinningBid(highestBid);
      }
    } catch (error) {
      console.error("Error fetching auction and bids:", error);
      toast.error("Failed to load auction details");
    } finally {
      setLoading(false);
    }
  };

  const calculateTimeRemaining = (auctionData = auction) => {
    if (!auctionData || !auctionData.seller_decision_deadline) {
      setTimeRemaining(null);
      return;
    }

    const deadline = new Date(auctionData.seller_decision_deadline);
    const now = new Date();
    const diff = deadline - now;

    if (diff <= 0) {
      setTimeRemaining({ hours: 0, minutes: 0 });
      return;
    }

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    setTimeRemaining({ hours, minutes });
  };

  const formatCurrency = (amount) => {
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount) || !isFinite(numAmount)) {
      return "₹0";
    }
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(numAmount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleString("en-IN", {
        year: "numeric",
        month: "numeric",
        day: "numeric",
        hour: "numeric",
        minute: "numeric",
        second: "numeric",
        hour12: true,
      });
    } catch (error) {
      return dateString;
    }
  };

  const getMediaUrl = (path) => {
    if (!path) return "";
    if (path.startsWith("http")) return path;
    return `${API_BASE_URL_MEDIA}${path.startsWith("/") ? "" : "/"}${path}`;
  };

  const handleAcceptBid = async () => {
    if (!canAcceptWinningBid) {
      toast.error("Accept Winning Bid is not available until winner approval is complete.");
      return;
    }
    if (!window.confirm("Are you sure you want to accept this winning bid?")) {
      return;
    }

    try {
      const response = await CommonService.post(API_URLS.AUCTION.ACCEPT_BID(auctionId), {});
      
      if (response && response.success) {
        toast.success("Winning bid accepted successfully!");
        // Redirect to Proforma Invoice page
        navigate(`/auction/${auctionId}/proforma-invoice`);
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message || error?.response?.data?.error || "Failed to accept bid";
      toast.error(errorMessage);
    }
  };

  const handleRejectBid = () => {
    setShowRejectModal(true);
  };

  const handleConfirmReject = async () => {
    try {
      const requestData = {};
      if (rejectionReason && rejectionReason.trim() !== "") {
        requestData.rejection_reason = rejectionReason.trim();
      }

      const response = await CommonService.post(API_URLS.AUCTION.REJECT_BID(auctionId), requestData);
      
      if (response && response.success) {
        toast.success("Winning bid rejected successfully!");
        setShowRejectModal(false);
        setRejectionReason("");
        // Redirect to My Auctions page
        navigate("/auction/my/auctions");
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message || error?.response?.data?.error || "Failed to reject bid";
      toast.error(errorMessage);
      console.error("Error rejecting bid:", error);
    }
  };

  const handleCancelReject = () => {
    setShowRejectModal(false);
    setRejectionReason("");
  };

  if (loading) {
    return <div className="winning-bid-review-page loading">Loading...</div>;
  }

  if (!auction || !winningBid) {
    return <div className="winning-bid-review-page error">Auction or winning bid not found</div>;
  }

  const winnerApprovalPending =
    auction.winner_approval_status === "pending" ||
    auction.status === "pending_winner_approval";
  const winnerApprovalRejected = auction.winner_approval_status === "rejected";
  const winnerApprovedNotDeclared =
    auction.winner_approval_status === "approved" && auction.status !== "ended";
  const canAcceptWinningBid =
    !winnerApprovalPending &&
    !winnerApprovalRejected &&
    !winnerApprovedNotDeclared &&
    (auction.winner_approval_status === "not_required" ||
      auction.winner_approval_status === "approved" ||
      !auction.winner_approval_status) &&
    Boolean(auction.winning_bid_id) &&
    auction.status === "ended";

  const totalBids = bids.length;
  const winningBidAmount = parseFloat(winningBid.bid_amount || winningBid.amount || 0);
  const reservePrice = parseFloat(auction.reserve_price || 0);
  const additionalProfit = winningBidAmount - reservePrice;
  const profitPercentage = reservePrice > 0 ? ((additionalProfit / reservePrice) * 100).toFixed(1) : 0;
  const platformFee = winningBidAmount * 0.025; // 2.5%

  const buyerName = winningBid.bidder_name || "Unknown Buyer";
  const buyerCompany = winningBid.bidder_company || "Unknown Company";
  const buyerEmail = winningBid.bidder_email || "";
  const buyerPhone = winningBid.bidder_phone || "";
  
  // Parse location from address JSON
  let buyerLocation = "";
  if (winningBid.bidder_address) {
    try {
      const address = typeof winningBid.bidder_address === 'string' 
        ? JSON.parse(winningBid.bidder_address) 
        : winningBid.bidder_address;
      
      const locationParts = [];
      if (address.city) locationParts.push(address.city);
      if (address.state) locationParts.push(address.state);
      
      buyerLocation = locationParts.join(", ");
    } catch (error) {
      console.error("Error parsing bidder address:", error);
    }
  }
  
  // Fallback to bidder_location if address parsing fails
  if (!buyerLocation && winningBid.bidder_location) {
    buyerLocation = winningBid.bidder_location;
  }

  // 72-hour deadline passed with no action: backend sets seller_decision to 'expired' on next getAuction
  const isDecisionExpired = auction.seller_decision === 'expired' || (
    auction.seller_decision_deadline && new Date(auction.seller_decision_deadline) <= new Date()
  );

  // Continue Auction Till End Date: show only for End on Scheduled Date Only and until end date
  const isEndOnScheduledOnly = auction.auction_end_condition === 'End on Scheduled Date Only';
  const isEndWhenReserveMet = auction.auction_end_condition === 'End When Reserve Met';
  const endDateReached = auction.end_date ? new Date(auction.end_date) <= new Date() : false;
  const showContinueAuctionButton = !isDecisionExpired && isEndOnScheduledOnly && !endDateReached;

  return (
    <div className="winning-bid-review-page">
      <div className="container">
        {/* Back Button */}
        <button className="back-btn" onClick={() => navigate(-1)}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="m12 19-7-7 7-7"></path>
            <path d="M19 12H5"></path>
          </svg>
          Back
        </button>

        {auction?.auction_id && (
          <LinkedLogisticsPanel
            sourceModule="auction"
            sourceRecordType="auction_award"
            sourceRecordId={String(auction.auction_id)}
            linkPurpose="fulfillment"
            title="Post-award logistics"
          />
        )}

        {/* Winner approval status */}
        {winnerApprovalPending && (
          <div
            className="winner-approval-banner"
            style={{
              marginBottom: "16px",
              padding: "14px 16px",
              borderRadius: "8px",
              background: "#EFF6FF",
              border: "1px solid #BFDBFE",
              color: "#1D4ED8",
              fontWeight: 600,
            }}
          >
            Awaiting Winner Approval — winner declaration is pending review by configured approvers.
          </div>
        )}
        {winnerApprovedNotDeclared && (
          <div
            className="winner-approval-banner approved"
            style={{
              marginBottom: "16px",
              padding: "14px 16px",
              borderRadius: "8px",
              background: "#ECFDF5",
              border: "1px solid #A7F3D0",
              color: "#047857",
              fontWeight: 600,
            }}
          >
            Winner approval is complete. Return to Review Bids and click Confirm &amp; Declare
            Winner to officially declare the highest valid bidder.
          </div>
        )}
        {winnerApprovalRejected && (
          <div
            className="winner-approval-banner rejected"
            style={{
              marginBottom: "16px",
              padding: "14px 16px",
              borderRadius: "8px",
              background: "#FEF2F2",
              border: "1px solid #FECACA",
              color: "#B91C1C",
              fontWeight: 600,
            }}
          >
            Winner declaration was rejected. Return to Review Bids to declare a winner again.
          </div>
        )}

        {/* Success Banner */}
        <div className="success-banner">
          <div className="banner-content">
            <div className="banner-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"></path>
                <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"></path>
                <path d="M4 22h16"></path>
                <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"></path>
                <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"></path>
                <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"></path>
              </svg>
            </div>
            <div className="banner-text">
              <h1>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="check-icon"
                >
                  <path d="M20 6 9 17l-5-5"></path>
                </svg>
                {winnerApprovalPending || winnerApprovedNotDeclared
                  ? "Winning Bid Review"
                  : "Auction Completed Successfully!"}
              </h1>
              <p>
                {winnerApprovalPending
                  ? "Winner declaration is awaiting approval. Accept Winning Bid will be available after approval and final winner declaration."
                  : winnerApprovedNotDeclared
                    ? "Approvers have approved the winner. Complete Confirm & Declare Winner on Review Bids before accepting the bid."
                    : "Your auction has ended with a winning bid. Review the details below and decide whether to accept the bid."}
              </p>
            </div>
          </div>
          <div className="banner-metrics">
            <div className="metric">
              <span className="metric-label">Total Bids Received</span>
              <span className="metric-value">{totalBids}</span>
            </div>
            <div className="metric">
              <span className="metric-label">Winning Bid</span>
              <span className="metric-value">{formatCurrency(winningBidAmount)}</span>
            </div>
            <div className="metric">
              <span className="metric-label">Above Reserve Price</span>
              <span className="metric-value positive">+{profitPercentage}%</span>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="main-content">
          {/* Left Column */}
          <div className="left-column">
            {/* Product Details */}
            <div className="section-card product-details">
              <div className="section-header orange">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                  <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
                  <line x1="12" y1="22.08" x2="12" y2="12"></line>
                </svg>
                <h2>Product Details</h2>
              </div>
              <div className="section-body">
                <div className="product-image">
                  {auction.images && auction.images.length > 0 ? (
                    <img
                      src={getMediaUrl(Array.isArray(auction.images) ? auction.images[0] : auction.images)}
                      alt={auction.product_name}
                    />
                  ) : (
                    <div className="placeholder-image">No Image</div>
                  )}
                </div>
                <div className="product-info">
                  <h3>{auction.product_name}</h3>
                  <p className="category">Category: {auction.category_name || "N/A"}</p>
                </div>
                <div className="financial-details">
                  <div className="financial-left">
                    <div className="financial-item">
                      <span className="label">Reserve Price</span>
                      <span className="value">{formatCurrency(reservePrice)}</span>
                    </div>
                    <div className="financial-item">
                      <span className="label">Additional Profit</span>
                      <span className="value positive">{formatCurrency(additionalProfit)}</span>
                    </div>
                  </div>
                  <div className="financial-right">
                    <div className="financial-item">
                      <span className="label">
                        Winning Bid
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="trophy-icon"
                        >
                          <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"></path>
                          <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"></path>
                          <path d="M4 22h16"></path>
                          <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"></path>
                          <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"></path>
                          <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"></path>
                        </svg>
                      </span>
                      <span className="value highlight">{formatCurrency(winningBidAmount)}</span>
                    </div>
                    <div className="financial-item">
                      <span className="label">Platform Fee (2.5%)</span>
                      <span className="value">{formatCurrency(platformFee)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Winning Buyer Details */}
            <div className="section-card buyer-details">
              <div className="section-header blue">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"></path>
                  <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"></path>
                  <path d="M4 22h16"></path>
                  <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"></path>
                  <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"></path>
                  <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"></path>
                </svg>
                <h2>Winning Buyer Details</h2>
              </div>
              <div className="section-body">
                <div className="buyer-header">
                  <div className="buyer-avatar">
                    {buyerName.charAt(0).toUpperCase()}
                  </div>
                  <div className="buyer-name-section">
                    <div className="buyer-name-row">
                      <h3>{buyerName}</h3>
                      <span className="verified-badge">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M20 6 9 17l-5-5"></path>
                        </svg>
                        Verified Buyer
                      </span>
                    </div>
                    <p className="buyer-company">{buyerCompany}</p>
                  </div>
                </div>
                <div className="buyer-contact-grid">
                  {buyerEmail && (
                    <div className="contact-item">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <rect width="20" height="16" x="2" y="4" rx="2"></rect>
                        <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"></path>
                      </svg>
                      <span>{buyerEmail}</span>
                    </div>
                  )}
                  {buyerPhone && (
                    <div className="contact-item">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                      </svg>
                      <span>{buyerPhone}</span>
                    </div>
                  )}
                  {buyerLocation && (
                    <div className="contact-item">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"></path>
                        <circle cx="12" cy="10" r="3"></circle>
                      </svg>
                      <span>{buyerLocation}</span>
                    </div>
                  )}
                  <div className="contact-item">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                    </svg>
                    <span>12 completed</span>
                  </div>
                </div>
                <div className="reliability-score">
                  <div className="score-header">
                    <div className="score-label-wrapper">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="shield-icon"
                      >
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                      </svg>
                      <span className="score-label">Buyer Reliability Score</span>
                    </div>
                    <span className="score-value">8.5/10</span>
                  </div>
                  <div className="score-bar">
                    <div className="score-fill score-85"></div>
                  </div>
                  <p className="score-description">Based on 12 completed transactions, payment history, and verification status.</p>
                </div>
              </div>
            </div>

            {/* Auction Timeline */}
            <div className="section-card timeline">
              <div className="section-header purple">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect width="18" height="18" x="3" y="4" rx="2" ry="2"></rect>
                  <line x1="16" y1="2" x2="16" y2="6"></line>
                  <line x1="8" y1="2" x2="8" y2="6"></line>
                  <line x1="3" y1="10" x2="21" y2="10"></line>
                </svg>
                <h2>Auction Timeline</h2>
              </div>
              <div className="section-body">
                <div className="timeline-item">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="clock-icon"
                  >
                    <circle cx="12" cy="12" r="10"></circle>
                    <polyline points="12 6 12 12 16 14"></polyline>
                  </svg>
                  <span>Auction Ended</span>
                  <span className="timeline-date">{formatDate(auction.end_date)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="right-column">
            {/* Review & Decision */}
            <div className="section-card review-decision">
              <div className="section-header orange">
                <h2>Review & Decision</h2>
              </div>
              <div className="section-body">
                {winnerApprovalPending ? (
                  <>
                    <p className="decision-text awaiting-approval-message">
                      <strong>Awaiting Winner Approval</strong> — winner declaration is pending review by
                      configured approvers. The Accept Winning Bid option will appear once approval is
                      complete and you have confirmed the final winner declaration on Review Bids.
                    </p>
                  </>
                ) : winnerApprovedNotDeclared ? (
                  <>
                    <p className="decision-text awaiting-approval-message">
                      <strong>Approval complete — declaration required.</strong> Return to Review Bids
                      and click Confirm &amp; Declare Winner before accepting the winning bid.
                    </p>
                    <div className="action-buttons">
                      <button
                        type="button"
                        className="btn-back-to-review"
                        onClick={() => navigate(`/auction/${auctionId}/review-bids`)}
                      >
                        Go to Review Bids
                      </button>
                    </div>
                  </>
                ) : winnerApprovalRejected ? (
                  <>
                    <p className="decision-text decision-expired-message">
                      <strong>Winner declaration rejected.</strong> Approvers did not approve this
                      winner. Return to Review Bids to declare a winner again.
                    </p>
                    <div className="action-buttons">
                      <button
                        type="button"
                        className="btn-back-to-review"
                        onClick={() => navigate(`/auction/${auctionId}/review-bids`)}
                      >
                        Back to Review Bids
                      </button>
                    </div>
                  </>
                ) : isDecisionExpired ? (
                  <>
                    <p className="decision-text decision-expired-message">
                      <strong>Decision period expired.</strong> You did not accept or reject the winning bid within 72 hours. The winning bid is no longer binding. You can go back to Review Bids to declare a different winner or take other action.
                    </p>
                    <div className="action-buttons">
                      <button
                        type="button"
                        className="btn-back-to-review"
                        onClick={() => navigate(`/auction/${auctionId}/review-bids`)}
                      >
                        Back to Review Bids
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="decision-text">Decision Required: You have <strong>72 hours</strong> to accept or reject this winning bid.</p>
                    {timeRemaining && (
                      <div className="time-remaining-box">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <circle cx="12" cy="12" r="10"></circle>
                          <polyline points="12 6 12 12 16 14"></polyline>
                        </svg>
                        <div>
                          <span className="time-label">Time Remaining</span>
                          <span className="time-value">{timeRemaining.hours}h {timeRemaining.minutes}m</span>
                        </div>
                      </div>
                    )}
                    <div className="action-buttons">
                      {canAcceptWinningBid && (
                        <button className="btn-accept" onClick={handleAcceptBid}>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M7 11v8a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-7a1 1 0 0 1 1-1h1a4 4 0 0 1 4 4Z"></path>
                            <path d="M15 7h1a3 3 0 0 1 0 6h-1"></path>
                            <path d="M11 12v6a1 1 0 0 0 1 1h1a1 1 0 0 0 1-1v-5a1 1 0 0 0-1-1h-2a1 1 0 0 0-1 1Z"></path>
                            <path d="M11 12V9a1 1 0 0 1 1-1h1a1 1 0 0 1 1 1v3"></path>
                          </svg>
                          Accept Winning Bid
                        </button>
                      )}
                      {showContinueAuctionButton && (
                        <button className="btn-continue" onClick={() => navigate(`/auction/${auctionId}`)}>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <circle cx="12" cy="12" r="10"></circle>
                            <polyline points="12 6 12 12 16 14"></polyline>
                          </svg>
                          Continue Auction Till End Date
                        </button>
                      )}
                      {canAcceptWinningBid && (
                      <button className="btn-reject" onClick={handleRejectBid}>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <line x1="18" y1="6" x2="6" y2="18"></line>
                          <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                        Reject Bid
                      </button>
                      )}
                    </div>
                  </>
                )}
                {!isDecisionExpired && !winnerApprovalPending && !winnerApprovedNotDeclared && (
                <div className="acceptance-info">
                  <h4>What happens after acceptance?</h4>
                  <ul>
                    <li>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M20 6 9 17l-5-5"></path>
                      </svg>
                      Buyer will be notified immediately
                    </li>
                    <li>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M20 6 9 17l-5-5"></path>
                      </svg>
                      Inspection phase begins (if opted)
                    </li>
                    <li>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M20 6 9 17l-5-5"></path>
                      </svg>
                      Payment will be processed
                    </li>
                    <li>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M20 6 9 17l-5-5"></path>
                      </svg>
                      Delivery coordination starts
                    </li>
                  </ul>
                </div>
                )}
              </div>
            </div>

            {/* Important Notice */}
            <div className="section-card important-notice">
              <div className="section-header orange">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                  <path d="M12 8v4"></path>
                  <path d="M12 16h.01"></path>
                </svg>
                <h2>Important Notice</h2>
              </div>
              <div className="section-body">
                <p>Trocanet operates as a neutral facilitator. We do not take ownership or custody of goods. All goods remain under seller ownership until buyer acceptance.</p>
              </div>
            </div>

            {/* Need Help */}
            <div className="section-card need-help">
              <h2>Need Help?</h2>
              <p>Contact our support team if you have questions about this bid.</p>
              <button className="btn-contact-support">
                Contact Support
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Reject Bid Confirmation Modal */}
      {showRejectModal && (
        <div className="reject-modal-overlay" onClick={handleCancelReject}>
          <div className="reject-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="reject-modal-header">
              <div className="reject-modal-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
              </div>
              <h2>Reject Winning Bid</h2>
              <button className="close-btn" onClick={handleCancelReject}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            <div className="reject-modal-body">
              <p className="warning-text">
                Are you sure you want to reject this winning bid? This action cannot be undone.
              </p>

              <div className="reject-warning-box">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                  <line x1="12" y1="9" x2="12" y2="13"></line>
                  <line x1="12" y1="17" x2="12.01" y2="17"></line>
                </svg>
                <div>
                  <strong>Warning:</strong> Rejecting this bid will:
                  <ul>
                    <li>Notify the buyer that their bid has been rejected</li>
                    <li>Prevent this bid from being accepted later</li>
                    <li>Allow you to consider other bids or extend the auction</li>
                  </ul>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="rejection-reason">
                  Rejection Reason <span className="optional">(Optional)</span>
                </label>
                <textarea
                  id="rejection-reason"
                  className="rejection-reason-input"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Please provide a reason for rejecting this bid (optional)..."
                  rows="4"
                />
                <p className="help-text">This reason will be shared with the buyer in the notification.</p>
              </div>
            </div>

            <div className="reject-modal-actions">
              <button className="btn-cancel" onClick={handleCancelReject}>
                Cancel
              </button>
              <button className="btn-confirm-reject" onClick={handleConfirmReject}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
                Confirm Rejection
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
